import { Skeleton, Tooltip } from '@heroui/react';
import { ReactNode } from 'react';

import { TriggerCategory } from '~/common/utils/triggerCategoryIcons';
import {
  TriggerIcon,
  type TriggerTooltipPipelineLike
} from '~/components/TriggerIcon';

export type PipelineInfosCellTrigger = TriggerTooltipPipelineLike & {
  category?: TriggerCategory;
  iconURL?: string;
};

export type PipelineInfosCellProps = {
  name: string;
  version: string;
  description?: string;
  trigger: PipelineInfosCellTrigger;
  /** Rendered after the version tag, such as the Digital Worker tag. */
  nameEndContent?: ReactNode;
};

export const PipelineInfosCell = ({
  name,
  version,
  description,
  trigger,
  nameEndContent
}: PipelineInfosCellProps) => {
  // On the name and the description only: the version and the tags after it have their own
  // tooltips, and two would open at once over them.
  const fullNameTooltip = {
    placement: 'bottom-start' as const,
    delay: 300,
    content: (
      <div
        data-testid='pipeline-infos-tooltip'
        className='flex max-w-sm flex-col gap-0.5 py-1'
      >
        <p className='break-words font-medium'>{name}</p>
        {description ? (
          <p className='break-words text-on-surface-variant'>{description}</p>
        ) : null}
      </div>
    )
  };

  return (
    <div className='flex w-[360px] items-center gap-3'>
      <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-[7.5px] bg-surface-dim'>
        <TriggerIcon
          triggerCategory={trigger.category}
          iconSrc={trigger.iconURL}
          pipeline={{
            // BACKEND-API-TRIGGER: drop extra fields; pass triggerSpec only.
            triggerSpec: trigger.triggerSpec,
            hasApiTrigger: trigger.hasApiTrigger,
            isUsingApiTrigger: trigger.isUsingApiTrigger,
            restApiTriggerRoutes: trigger.restApiTriggerRoutes,
            flowSpec: trigger.flowSpec
          }}
        />
      </div>
      <div className='flex w-full min-w-0 flex-col'>
        <div className='flex gap-2'>
          <Tooltip {...fullNameTooltip}>
            <p className='min-w-0 truncate'>{name}</p>
          </Tooltip>
          <div className='h-fit shrink-0 rounded-small bg-surface-dim px-1 py-0.5 text-xs font-medium tracking-[0.25px]'>
            {version}
          </div>
          {nameEndContent}
        </div>
        <Tooltip {...fullNameTooltip}>
          <p className='truncate font-regular tracking-wide text-on-surface-variant'>
            {description}
          </p>
        </Tooltip>
      </div>
    </div>
  );
};

export const PipelineInfosCellSkeleton = () => (
  <div className='flex w-[360px] items-center gap-3'>
    <Skeleton className='h-10 w-10 shrink-0 rounded-[7.5px]' />
    <div className='flex w-full min-w-0 flex-col gap-2'>
      <div className='flex gap-2'>
        <Skeleton className='h-4 w-32 rounded' />
        <Skeleton className='h-5 w-8 rounded-small' />
      </div>
      <Skeleton className='h-3 w-48 rounded' />
    </div>
  </div>
);
